/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useState } from 'react';
import { useFeedback } from '../contexts/FeedbackContext';
import { useSession } from '../contexts/SessionContext';
import {
  type AdminOrganizationRow,
  type AdminUserRow,
  type SystemConfigRow,
  getAdminSystemConfig,
  listAdminOrganizations,
  listAdminUsers,
  patchAdminOrganization,
  patchAdminUser,
  updateAdminSystemConfig
} from '../api';
import { formatAbsoluteDate, formatRelativeDate } from '../utils/formatters';

type AdminPage = 'config' | 'users' | 'organizations';

function Toggle({
  on,
  onLabel,
  offLabel,
  onChange,
  disabled
}: {
  on: boolean;
  onLabel: string;
  offLabel: string;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={() => onChange(!on)}
      disabled={disabled}
      className={on ? 'statusChip tone-success' : 'statusChip tone-neutral'}
      style={{ cursor: disabled ? 'default' : 'pointer', border: 0, fontFamily: 'inherit' }}
    >
      {on ? onLabel : offLabel}
    </button>
  );
}

function ConfigBooleanRow({
  row,
  onToggle,
  busy
}: {
  row: SystemConfigRow;
  onToggle: (key: string, value: boolean) => void;
  busy: boolean;
}) {
  const val = row.value === 'true';
  const label = row.key.replace(/_/g, ' ');
  const descriptions: Record<string, string> = {
    signups_enabled: 'Allow new users to create accounts via the sign up page.',
    invite_only_mode: 'Restrict new accounts to invited users only.',
    maintenance_mode: 'Block all logins and API access for non-admin users. Super admins are unaffected.'
  };
  return (
    <div className="tableRow" style={{ alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, fontSize: 13, textTransform: 'capitalize' }}>{label}</div>
        {descriptions[row.key] && (
          <div style={{ color: 'var(--text-faint)', fontSize: 12, marginTop: 2 }}>{descriptions[row.key]}</div>
        )}
      </div>
      {row.lastModifiedBy && (
        <span style={{ color: 'var(--text-faint)', fontSize: 11 }}>by {row.lastModifiedBy}</span>
      )}
      <Toggle on={val} onLabel="Enabled" offLabel="Disabled" onChange={(next) => onToggle(row.key, next)} disabled={busy} />
    </div>
  );
}

function ConfigTextRow({
  row,
  onSave,
  busy
}: {
  row: SystemConfigRow;
  onSave: (key: string, value: string) => void;
  busy: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row.value);

  useEffect(() => { setDraft(row.value); }, [row.value]);

  const label = row.key.replace(/_/g, ' ');

  return (
    <div className="tableRow" style={{ alignItems: 'center', gap: 8 }}>
      <span style={{ flex: '0 0 200px', fontWeight: 500, fontSize: 13, textTransform: 'capitalize' }}>{label}</span>
      {editing ? (
        <>
          <input
            style={{ flex: 2, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-mid)', borderRadius: 6, color: 'var(--text)', padding: '5px 10px', fontSize: 13, fontFamily: 'inherit' }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
          />
          <button className="primaryButton" style={{ padding: '5px 14px', fontSize: 13 }} disabled={busy} onClick={() => { onSave(row.key, draft); setEditing(false); }}>Save</button>
          <button className="secondaryButton" style={{ padding: '5px 14px', fontSize: 13 }} onClick={() => { setDraft(row.value); setEditing(false); }}>Cancel</button>
        </>
      ) : (
        <>
          <code style={{ flex: 2, color: 'var(--text-soft)', fontSize: 12, wordBreak: 'break-all' }}>{row.value || <em style={{ color: 'var(--text-faint)' }}>empty</em>}</code>
          {row.lastModifiedBy && (
            <span style={{ color: 'var(--text-faint)', fontSize: 11, marginRight: 8 }}>by {row.lastModifiedBy}</span>
          )}
          <button className="secondaryButton" style={{ padding: '5px 14px', fontSize: 13 }} onClick={() => setEditing(true)}>Edit</button>
        </>
      )}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 8, padding: '10px 14px', color: 'var(--red, #f87171)', fontSize: 13, marginBottom: 16 }}>
      {message}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>
      {message}
    </div>
  );
}

export function SuperAdminPage() {
  const { session } = useSession();
  const { confirm, toast } = useFeedback();
  const token = session?.accessToken ?? '';
  const adminUser = session?.user;

  const [page, setPage] = useState<AdminPage>('config');

  const [config, setConfig] = useState<SystemConfigRow[]>([]);
  const [configLoading, setConfigLoading] = useState(true);
  const [configBusy, setConfigBusy] = useState(false);
  const [configError, setConfigError] = useState('');

  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');

  const [orgs, setOrgs] = useState<AdminOrganizationRow[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsError, setOrgsError] = useState('');

  function extractErrorMessage(err: unknown): string {
    const e = err as any;
    const msg = e?.response?.data?.message ?? e?.message ?? 'An unexpected error occurred';
    return typeof msg === 'string' ? msg : JSON.stringify(msg);
  }

  const loadConfig = useCallback(async () => {
    setConfigLoading(true); setConfigError('');
    try { setConfig(await getAdminSystemConfig(token)); }
    catch (err) { setConfigError(extractErrorMessage(err)); }
    finally { setConfigLoading(false); }
  }, [token]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true); setUsersError('');
    try { setUsers(await listAdminUsers(token)); }
    catch (err) { setUsersError(extractErrorMessage(err)); }
    finally { setUsersLoading(false); }
  }, [token]);

  const loadOrgs = useCallback(async () => {
    setOrgsLoading(true); setOrgsError('');
    try { setOrgs(await listAdminOrganizations(token)); }
    catch (err) { setOrgsError(extractErrorMessage(err)); }
    finally { setOrgsLoading(false); }
  }, [token]);

  useEffect(() => { if (token) void loadConfig(); }, [token, loadConfig]);
  useEffect(() => { if (token && page === 'users') void loadUsers(); }, [token, page, loadUsers]);
  useEffect(() => { if (token && page === 'organizations') void loadOrgs(); }, [token, page, loadOrgs]);

  async function handleToggleConfig(key: string, value: boolean) {
    setConfigBusy(true); setConfigError('');
    try {
      const updated = await updateAdminSystemConfig(token, key, String(value));
      setConfig((prev) => prev.map((r) => r.key === key ? updated : r));
    } catch (err) {
      setConfigError(extractErrorMessage(err));
    } finally { setConfigBusy(false); }
  }

  async function handleSaveConfig(key: string, value: string) {
    setConfigBusy(true); setConfigError('');
    try {
      const updated = await updateAdminSystemConfig(token, key, value);
      setConfig((prev) => prev.map((r) => r.key === key ? updated : r));
    } catch (err) {
      setConfigError(extractErrorMessage(err));
    } finally { setConfigBusy(false); }
  }

  async function handleToggleUser(userId: string, isActive: boolean) {
    try {
      const updated = await patchAdminUser(token, userId, { isActive });
      setUsers((prev) => prev.map((u) => u.tenantUserId === userId ? updated : u));
    } catch (err) {
      setUsersError(extractErrorMessage(err));
    }
  }

  async function handleToggleSuperAdmin(target: AdminUserRow, isSuperAdmin: boolean) {
    const verb = isSuperAdmin ? 'Grant' : 'Revoke';
    const confirmed = await confirm({
      title: `${verb} super-admin access?`,
      message: `${target.email} will ${isSuperAdmin ? 'gain' : 'lose'} full control over every organisation, user, and system setting on this instance.`,
      confirmLabel: `${verb} access`,
      danger: true
    });
    if (!confirmed) return;

    try {
      const updated = await patchAdminUser(token, target.tenantUserId, { isSuperAdmin });
      setUsers((prev) => prev.map((u) => u.tenantUserId === target.tenantUserId ? updated : u));
      toast('success', `Super-admin access ${isSuperAdmin ? 'granted to' : 'revoked from'} ${target.email}`);
    } catch (err) {
      setUsersError(extractErrorMessage(err));
    }
  }

  async function handleToggleOrg(organizationId: string, isActive: boolean) {
    try {
      const updated = await patchAdminOrganization(token, organizationId, { isActive });
      setOrgs((prev) => prev.map((o) => o.tenantOrganizationId === organizationId ? updated : o));
    } catch (err) {
      setOrgsError(extractErrorMessage(err));
    }
  }

  const booleanKeys = new Set(['signups_enabled', 'invite_only_mode', 'maintenance_mode']);
  const navItems: { id: AdminPage; label: string }[] = [
    { id: 'config', label: 'System Config' },
    { id: 'users', label: 'Users' },
    { id: 'organizations', label: 'Organizations' }
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', minHeight: '100vh' }}>
      <nav className="sidebar">
        <div className="brandBlock" style={{ cursor: 'default', pointerEvents: 'none' }}>
          <div className="brandMark">C</div>
          <div style={{ minWidth: 0 }}>
            <div className="brandName">Cindro</div>
            <div className="brandSub" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>Super Admin</div>
          </div>
        </div>

        <div className="sidebarDivider" />

        {navItems.map(({ id, label }) => (
          <button key={id} className={page === id ? 'navRow active' : 'navRow'} onClick={() => setPage(id)} style={{ width: '100%', textAlign: 'left' }}>
            <span className="navRowLabel">{label}</span>
          </button>
        ))}

        <div style={{ flex: 1 }} />

        {adminUser && (
          <div style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-faint)', borderTop: '1px solid var(--border)' }}>
            <div style={{ color: 'var(--text-soft)', marginBottom: 2, fontWeight: 500 }}>{adminUser.displayName}</div>
            <div>{adminUser.email}</div>
          </div>
        )}
      </nav>

      <main style={{ padding: '32px 40px', maxWidth: 900 }}>
        {page === 'config' && (
          <>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.03em' }}>System Config</h2>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-soft)' }}>Global instance settings. Changes take effect immediately.</p>
            </div>

            {configError && <ErrorBanner message={configError} />}

            {configLoading ? (
              <p style={{ color: 'var(--text-faint)', fontSize: 13 }}>Loading…</p>
            ) : (
              <>
                <div style={{ display: 'grid', gap: 6 }}>
                  {config.filter((r) => booleanKeys.has(r.key)).map((row) => (
                    <ConfigBooleanRow key={row.key} row={row} onToggle={handleToggleConfig} busy={configBusy} />
                  ))}
                </div>

                {config.filter((r) => !booleanKeys.has(r.key)).length > 0 && (
                  <>
                    <div style={{ margin: '20px 0', borderTop: '1px solid var(--border)' }} />
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-faint)' }}>Advanced</div>
                    </div>
                    <div style={{ display: 'grid', gap: 6 }}>
                      {config.filter((r) => !booleanKeys.has(r.key)).map((row) => (
                        <ConfigTextRow key={row.key} row={row} onSave={handleSaveConfig} busy={configBusy} />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}

        {page === 'users' && (
          <>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.03em' }}>Users</h2>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-soft)' }}>All registered accounts on this instance.</p>
            </div>

            {usersError && <ErrorBanner message={usersError} />}

            {usersLoading ? (
              <p style={{ color: 'var(--text-faint)', fontSize: 13 }}>Loading…</p>
            ) : (
              <>
                <div className="tableHead" style={{ gridTemplateColumns: '1fr 180px 120px 100px' }}>
                  <span>Email / Name</span>
                  <span>Last Login</span>
                  <span>Super Admin</span>
                  <span>Status</span>
                </div>
                {users.length === 0 && !usersError && <EmptyState message="No users found." />}
                {users.map((u) => (
                  <div key={u.tenantUserId} className="tableRow" style={{ gridTemplateColumns: '1fr 180px 120px 100px', alignItems: 'center' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 1 }}>{u.displayName}</div>
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--text-faint)' }} title={u.lastLoginAt ? formatAbsoluteDate(u.lastLoginAt) : undefined}>
                      {u.lastLoginAt ? formatRelativeDate(u.lastLoginAt) : 'Never'}
                    </span>
                    {u.tenantUserId === adminUser?.tenantUserId ? (
                      <span style={{ fontSize: 12, color: 'var(--green, #4ade80)' }} title="You cannot change your own super-admin access">
                        Yes (you)
                      </span>
                    ) : (
                      <Toggle
                        on={u.isSuperAdmin}
                        onLabel="Admin"
                        offLabel="—"
                        onChange={(next) => void handleToggleSuperAdmin(u, next)}
                      />
                    )}
                    <Toggle on={u.isActive} onLabel="Active" offLabel="Inactive" onChange={(next) => void handleToggleUser(u.tenantUserId, next)} />
                  </div>
                ))}
              </>
            )}
          </>
        )}

        {page === 'organizations' && (
          <>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.03em' }}>Organizations</h2>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-soft)' }}>All tenant organizations on this instance.</p>
            </div>

            {orgsError && <ErrorBanner message={orgsError} />}

            {orgsLoading ? (
              <p style={{ color: 'var(--text-faint)', fontSize: 13 }}>Loading…</p>
            ) : (
              <>
                <div className="tableHead" style={{ gridTemplateColumns: '1fr 120px 80px 120px 100px' }}>
                  <span>Name / Slug</span>
                  <span>Created</span>
                  <span>Region</span>
                  <span>Data Plane</span>
                  <span>Status</span>
                </div>
                {orgs.length === 0 && !orgsError && <EmptyState message="No organizations found." />}
                {orgs.map((o) => (
                  <div key={o.tenantOrganizationId} className="tableRow" style={{ gridTemplateColumns: '1fr 120px 80px 120px 100px', alignItems: 'center' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 1 }}>{o.slug}</div>
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--text-faint)' }} title={o.createdAt ? formatAbsoluteDate(o.createdAt) : undefined}>
                      {o.createdAt ? formatRelativeDate(o.createdAt) : '—'}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-soft)' }}>{o.region}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-soft)' }}>{o.dataPlaneMode}</span>
                    <Toggle on={o.isActive} onLabel="Active" offLabel="Suspended" onChange={(next) => void handleToggleOrg(o.tenantOrganizationId, next)} />
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
